import { useEffect, useRef, useState } from "react";
import { useTranslations } from "use-intl";
import { Bell, Swords } from "lucide-react";
import { cn } from "@/utils/cn";
import { useRouter } from "@/i18n/navigation";
import { useChallengeInvites, useRespondToInvite } from "@/features/challenges/use-challenge-invites";
import { AnnouncementLink } from "./announcement-link";
import { useAnnouncements, useMarkAnnouncementsRead, type StudentAnnouncement } from "./use-announcements";

const TONE_DOT: Record<StudentAnnouncement["tone"], string> = {
  info: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
};

/**
 * Header bell for signed-in students: an unread count, and a panel listing
 * recent announcements. Opening the panel marks what's shown as read.
 */
export function NotificationBell() {
  const t = useTranslations("common.notifications");
  const tInvites = useTranslations("exams.challengesHub.invites");
  const router = useRouter();
  const invites = useChallengeInvites().data?.invites ?? [];
  const respond = useRespondToInvite();
  const { data } = useAnnouncements();
  const markRead = useMarkAnnouncementsRead();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Open challenge invites count as unread until answered.
  const unread = (data?.unreadCount ?? 0) + invites.length;
  const items = data?.announcements ?? [];

  function toggle() {
    const next = !open;
    setOpen(next);
    const unreadIds = items.filter((a) => !a.isRead).map((a) => a.id);
    if (next && unreadIds.length > 0) markRead.mutate(unreadIds);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={unread > 0 ? t("labelUnread", { count: unread }) : t("label")}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Bell className="h-[18px] w-[18px]" aria-hidden="true" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        // On phones the bell sits on the left of the header, so a panel anchored to its right edge
        // ran off the screen; there it is pinned to both screen edges under the header instead.
        <div className="fixed inset-x-4 top-16 z-50 overflow-hidden rounded-2xl border border-border bg-card shadow-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-11 sm:w-[22rem]">
          <div className="border-b border-border px-4 py-3 text-sm font-semibold">{t("title")}</div>
          {invites.length > 0 && (
            <ul className="divide-y divide-border border-b border-border bg-primary/5">
              {invites.map((invite) => (
                <li key={invite.id} className="flex gap-3 px-4 py-3">
                  <Swords className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <p className="text-sm">
                      <span className="font-semibold">{tInvites("title", { name: invite.fromName ?? tInvites("someone") })}</span>
                      <span className="block truncate text-xs text-muted-foreground">{invite.examTitle}</span>
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={respond.isPending}
                        onClick={() =>
                          respond.mutate(
                            { id: invite.id, accept: true },
                            {
                              onSuccess: (result) => {
                                setOpen(false);
                                router.push(`/challenge/${result.code}`);
                              },
                            },
                          )
                        }
                        className="rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary-hover"
                      >
                        {tInvites("accept")}
                      </button>
                      <button
                        type="button"
                        disabled={respond.isPending}
                        onClick={() => respond.mutate({ id: invite.id, accept: false })}
                        className="rounded-lg border border-border px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted"
                      >
                        {tInvites("decline")}
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {items.length === 0 && invites.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t("empty")}</p>
          ) : (
            <ul className="max-h-96 divide-y divide-border overflow-y-auto">
              {items.map((item) => (
                <li key={item.id} className={cn("flex gap-3 px-4 py-3", !item.isRead && "bg-primary/5")}>
                  <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", TONE_DOT[item.tone])} aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{item.title}</p>
                    <p className="mt-0.5 whitespace-pre-line text-sm text-muted-foreground">{item.body}</p>
                    {item.linkUrl && item.linkLabel && (
                      <AnnouncementLink url={item.linkUrl} label={item.linkLabel} className="mt-2" onNavigate={() => setOpen(false)} />
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">{new Date(item.createdAt).toLocaleDateString()}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
